import {
  type ColumnDef,
  type OnChangeFn,
  type RowSelectionState,
  type Table,
  type TableOptions,
  type TableState,
  type Updater,
  functionalUpdate,
} from '@tanstack/react-table';

import {
  type DataTableOptions,
  dataTableCheckboxColumn,
} from '@spotify-internal/data-table';

export type DataTableSelection<TData> = {
  checkboxHeader?: ColumnDef<TData>['header'];
  rowSelection: RowSelectionState;
  onRowSelectionChange: OnChangeFn<RowSelectionState>;
  enableRowSelection?: TableOptions<TData>['enableRowSelection'];
};

type DataTableSelectionOptions<TData> = Pick<
  DataTableOptions<TData, string>,
  'checkboxOnlySelect'
> &
  Pick<TableOptions<TData>, 'enableRowSelection' | 'onRowSelectionChange'> & {
    checkboxColumns: ColumnDef<TData>[];
    checkboxColumnIds: string[];
    rowSelection: RowSelectionState;
  };

export const createDataTableSelectionOptions = <TData>({
  selection,
  rowSelection = {},
}: {
  selection?: DataTableSelection<TData>;
  rowSelection?: RowSelectionState;
}): DataTableSelectionOptions<TData> => {
  if (!selection)
    return {
      checkboxColumns: [],
      checkboxColumnIds: [],
      rowSelection,
      enableRowSelection: false,
    };
  return {
    checkboxColumns: [
      {
        ...dataTableCheckboxColumn,
        header: selection.checkboxHeader ?? dataTableCheckboxColumn.header,
      },
    ],
    checkboxColumnIds: [dataTableCheckboxColumn.id],
    rowSelection: selection.rowSelection,
    enableRowSelection: selection.enableRowSelection ?? true,
    onRowSelectionChange: selection.onRowSelectionChange,
    checkboxOnlySelect: true,
  };
};

export const applyDataTableSelectionChange = <TData>(
  table: Table<TData>,
  updater: Updater<TableState>,
  selection?: DataTableSelection<TData>,
): void => {
  if (!selection) return;

  const currentState = table.getState();
  const nextState = functionalUpdate(updater, currentState);
  if (nextState.rowSelection === currentState.rowSelection) return;

  const rowsById = table.getCoreRowModel().rowsById;
  selection.onRowSelectionChange(currentSelection => {
    const { rowSelection } = functionalUpdate(updater, {
      ...currentState,
      rowSelection: currentSelection,
    });
    return Object.fromEntries(
      Object.entries(rowSelection).filter(
        ([id, selected]) => selected && (rowsById[id]?.getCanSelect() ?? true),
      ),
    );
  });
};
